import type { TFunction } from 'i18next';
import { DateTime } from 'luxon';

import { formatDate } from '@/utils/format';

export type GatheringKind = 'service' | 'program';

export type ServicePattern =
  | 'weekly'
  | 'fortnight'
  | 'first'
  | 'second'
  | 'third'
  | 'fourth'
  | 'last'
  | 'special'
  | 'dates';

export interface Gathering {
  id: string;
  name: string;
  kind: GatheringKind;
  category: string;
  scheduleType: string;
  scheduleRule: string | null;
  startsOn: string | null;
  endsOn: string | null;
  startTime: string | null;
  endTime: string | null;
  venueId: string | null;
  venueName: string | null;
  joinUrl?: string | null;
  joinRequiresCode?: boolean;
  joinCode?: string | null;
  coverImageKey: string | null;
  coverImageUrl: string | null;
  /** `internal` stays on the church calendar only. Other values appear in the app once published. */
  visibility?: 'public' | 'members_only' | 'internal';
  isActive: boolean;
  description: string | null;
  /** Default for enabling GPS check-in when opening a session for this gathering. */
  gpsAutoCheckInEnabled?: boolean;
  registrationMode?: 'none' | 'rsvp' | 'registration';
  registrationAccess?: 'open' | 'invite_only';
  registrationFeeType?: 'free' | 'paid';
  registrationFeeAmountMinor?: number | null;
  registrationFeeCurrency?: string | null;
  registrationFundId?: string | null;
  registrationAllowedPaymentMethods?: Array<'mobile_money' | 'card' | 'cash'>;
  capacity?: number | null;
  /** Present on detail fetch; false when attendance, giving, or other activity is linked. */
  canDelete?: boolean;
  createdAt: string;
  updatedAt: string;
}

export const WEEKDAYS = ['7', '1', '2', '3', '4', '5', '6'] as const;

export const SERVICE_PATTERNS: ServicePattern[] = [
  'weekly',
  'fortnight',
  'first',
  'second',
  'third',
  'fourth',
  'last',
  'special',
];

export const PROGRAM_PATTERNS: ServicePattern[] = [...SERVICE_PATTERNS, 'dates'];

export const patternToNth: Record<
  Exclude<ServicePattern, 'weekly' | 'fortnight' | 'special' | 'dates'>,
  string
> = {
  first: '1',
  second: '2',
  third: '3',
  fourth: '4',
  last: '-1',
};

export const nextWeekdayOnOrAfter = (fromDate: string, weekday: string): string => {
  let cursor = DateTime.fromISO(fromDate).startOf('day');
  const target = Number(weekday);
  while (cursor.weekday !== target) {
    cursor = cursor.plus({ days: 1 });
  }
  return cursor.toISODate() ?? fromDate;
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const parseCustomScheduleDates = (rule: string | null | undefined): string[] => {
  if (!rule?.trim()) return [];
  const parts = rule.split(',').map((part) => part.trim()).filter(Boolean);
  return [...new Set(parts.filter((part) => DATE_ONLY.test(part)))].sort();
};

export const parseServicePattern = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule'>,
): { pattern: ServicePattern; weekday: string; specificDates: string[] } => {
  if (row.scheduleType === 'custom') {
    return {
      pattern: 'dates',
      weekday: '7',
      specificDates: parseCustomScheduleDates(row.scheduleRule),
    };
  }
  if (row.scheduleType === 'one_off') {
    return { pattern: 'special', weekday: '7', specificDates: [] };
  }
  const nth = /^nth:(-?[1-4]):([1-7])$/.exec(row.scheduleRule ?? '');
  if (row.scheduleType === 'monthly' && nth?.[1] && nth[2]) {
    const map: Record<string, ServicePattern> = {
      '1': 'first',
      '2': 'second',
      '3': 'third',
      '4': 'fourth',
      '-1': 'last',
    };
    return {
      pattern: map[nth[1]] ?? 'first',
      weekday: nth[2],
      specificDates: [],
    };
  }
  const fortnight = /^fortnight:([1-7])$/.exec(row.scheduleRule ?? '');
  if (row.scheduleType === 'weekly' && fortnight?.[1]) {
    return { pattern: 'fortnight', weekday: fortnight[1], specificDates: [] };
  }
  const weekday =
    row.scheduleRule && /^[1-7]$/.test(row.scheduleRule) ? row.scheduleRule : '7';
  return { pattern: 'weekly', weekday, specificDates: [] };
};

export const scheduleText = (
  row: Pick<Gathering, 'kind' | 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
  t: TFunction<'programs'>,
): string => {
  if (row.scheduleType === 'custom') {
    const dates = parseCustomScheduleDates(row.scheduleRule);
    if (dates.length === 0) {
      return t('programs.patterns.dates');
    }
    if (dates.length <= 3) {
      return t('programs.scheduleSpecificDaysShort', {
        dates: dates.map((date) => formatDate(date)).join(', '),
      });
    }
    return t('programs.scheduleSpecificDays', { count: dates.length });
  }
  if (row.scheduleType === 'one_off') {
    const start = row.startsOn ?? row.scheduleRule;
    const end = row.endsOn ?? start;
    if (!start) {
      return row.kind === 'service'
        ? t('services.patterns.special')
        : t('programs.patterns.special');
    }
    if (!end || end === start) {
      return row.kind === 'service'
        ? t('services.scheduleSpecial', { date: formatDate(start) })
        : t('programs.scheduleOneDay', { date: formatDate(start) });
    }
    return t('programs.scheduleSpan', { start: formatDate(start), end: formatDate(end) });
  }
  const parsed = parseServicePattern(row);
  if (parsed.pattern === 'weekly') {
    return t('services.scheduleWeekly', { weekday: t(`weekdays.${parsed.weekday}`) });
  }
  if (parsed.pattern === 'fortnight') {
    return t('services.scheduleFortnight', { weekday: t(`weekdays.${parsed.weekday}`) });
  }
  const nthKey =
    patternToNth[parsed.pattern as Exclude<ServicePattern, 'weekly' | 'fortnight' | 'special' | 'dates'>];
  return t('services.scheduleNth', {
    nth: t(`services.nth.${nthKey}`),
    weekday: t(`weekdays.${parsed.weekday}`),
  });
};

export const listPathForKind = (kind: GatheringKind) =>
  kind === 'service' ? '/services' : '/programs';

export const detailPathFor = (kind: GatheringKind, id: string) =>
  kind === 'service' ? `/services/${id}` : `/programs/${id}`;

const datesInRange = (startsOn: string, endsOn: string): string[] => {
  let cursor = DateTime.fromISO(startsOn).startOf('day');
  const end = DateTime.fromISO(endsOn).startOf('day');
  if (!cursor.isValid || !end.isValid || end < cursor) return [];
  const dates: string[] = [];
  while (cursor <= end) {
    const iso = cursor.toISODate();
    if (iso) dates.push(iso);
    cursor = cursor.plus({ days: 1 });
  }
  return dates;
};

const nthWeekdayOfMonth = (month: DateTime, nth: number, weekday: number): DateTime | null => {
  if (nth === -1) {
    let cursor = month.endOf('month').startOf('day');
    while (cursor.weekday !== weekday) {
      cursor = cursor.minus({ days: 1 });
    }
    return cursor.month === month.month ? cursor : null;
  }
  const firstOfMonth = month.startOf('month');
  const offset = (weekday - firstOfMonth.weekday + 7) % 7;
  const candidate = firstOfMonth.plus({ days: offset + (nth - 1) * 7 });
  return candidate.month === month.month ? candidate : null;
};

/** Dates that define the gathering (range or picked days), not every recurring instance. */
export const scheduleSummaryDatesForGathering = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
): string[] => {
  if (row.scheduleType === 'one_off') {
    if (row.startsOn && row.endsOn && row.endsOn !== row.startsOn) {
      return datesInRange(row.startsOn, row.endsOn);
    }
    const date = row.startsOn ?? row.scheduleRule;
    return date ? [date] : [];
  }
  if (row.scheduleType === 'custom') {
    return parseCustomScheduleDates(row.scheduleRule);
  }
  return [];
};

/** Count of dates that belong to the gathering schedule (not every recurring instance). */
export const scheduleDateCountForGathering = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
): number | null => {
  if (row.scheduleType === 'one_off') {
    if (row.startsOn && row.endsOn && row.endsOn !== row.startsOn) {
      return datesInRange(row.startsOn, row.endsOn).length;
    }
    return row.startsOn || row.scheduleRule ? 1 : 0;
  }
  if (row.scheduleType === 'custom') {
    return parseCustomScheduleDates(row.scheduleRule).length;
  }
  return null;
};

/** Dates that can be chosen when recording offerings against a gathering schedule. */
export const choosableOfferingDates = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
  today: string,
  datesOnList: string[],
  pastHorizonDays = 366,
): string[] => {
  const onList = new Set(datesOnList);

  if (row.scheduleType === 'one_off' || row.scheduleType === 'custom') {
    return scheduleSummaryDatesForGathering(row)
      .filter((date) => !onList.has(date))
      .sort();
  }

  const pastStart =
    row.startsOn ??
    DateTime.fromISO(today).minus({ days: pastHorizonDays }).toISODate() ??
    today;
  const futureEnd =
    DateTime.fromISO(today).plus({ days: pastHorizonDays }).toISODate() ?? today;
  const scheduleDates = scheduleDatesForGathering(row, pastStart, futureEnd);

  const pastAndToday = scheduleDates.filter((date) => date <= today && !onList.has(date));
  const nextFuture = scheduleDates.find((date) => date > today && !onList.has(date));

  const selectable = [...pastAndToday];
  if (nextFuture) selectable.push(nextFuture);

  return [...new Set(selectable)].sort();
};

/** Schedule dates marked on the offerings calendar. */
export const offeringCalendarMarkedDates = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
  today: string,
  pastHorizonDays = 366,
): string[] => {
  if (row.scheduleType === 'one_off' || row.scheduleType === 'custom') {
    return scheduleSummaryDatesForGathering(row);
  }
  const pastStart =
    row.startsOn ??
    DateTime.fromISO(today).minus({ days: pastHorizonDays }).toISODate() ??
    today;
  const futureEnd =
    DateTime.fromISO(today).plus({ days: pastHorizonDays }).toISODate() ?? today;
  return scheduleDatesForGathering(row, pastStart, futureEnd);
};

/** Every day in range that is not selectable — drives the calendar disabled state. */
export const offeringCalendarDisabledDates = (
  selectableDates: string[],
  rangeStart: string,
  rangeEnd: string,
): string[] => {
  const selectable = new Set(selectableDates);
  const disabled: string[] = [];
  let cursor = DateTime.fromISO(rangeStart).startOf('day');
  const end = DateTime.fromISO(rangeEnd).startOf('day');
  while (cursor <= end) {
    const iso = cursor.toISODate();
    if (iso && !selectable.has(iso)) disabled.push(iso);
    cursor = cursor.plus({ days: 1 });
  }
  return disabled;
};

export const defaultOfferingDate = (
  selectableDates: string[],
  today: string,
): string | null => {
  if (selectableDates.length === 0) return null;
  const pastOrToday = selectableDates.filter((date) => date <= today);
  if (pastOrToday.length > 0) return pastOrToday[pastOrToday.length - 1] ?? null;
  return selectableDates[0] ?? null;
};

/** Dates that can be picked when adding day details — matches the gathering schedule. */
export const choosableDayDetailDates = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
  fromDate: string,
  horizonDays = 366,
): string[] => {
  if (row.scheduleType === 'one_off' || row.scheduleType === 'custom') {
    return scheduleSummaryDatesForGathering(row);
  }
  const toDate =
    DateTime.fromISO(fromDate).plus({ days: horizonDays }).toISODate() ?? fromDate;
  return scheduleDatesForGathering(row, fromDate, toDate);
};

export const scheduleDatesForGathering = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn'>,
  fromDate?: string,
  toDate?: string,
): string[] => {
  const rangeStart = fromDate
    ? DateTime.fromISO(fromDate).startOf('day')
    : DateTime.now().startOf('day');
  const rangeEnd = toDate
    ? DateTime.fromISO(toDate).startOf('day')
    : rangeStart.plus({ days: 366 });

  let dates: string[] = [];

  if (row.scheduleType === 'one_off') {
    if (row.startsOn && row.endsOn && row.endsOn !== row.startsOn) {
      dates = datesInRange(row.startsOn, row.endsOn);
    } else {
      const date = row.startsOn ?? row.scheduleRule;
      if (date) dates = [date];
    }
  } else if (row.scheduleType === 'custom') {
    dates = parseCustomScheduleDates(row.scheduleRule);
  } else if (row.scheduleType === 'weekly') {
    const fortnight = /^fortnight:([1-7])$/.exec(row.scheduleRule ?? '');
    if (fortnight?.[1]) {
      const weekday = Number(fortnight[1]);
      const anchor = row.startsOn ?? rangeStart.toISODate();
      if (anchor) {
        let anchorDay = DateTime.fromISO(anchor).startOf('day');
        while (anchorDay.weekday !== weekday) {
          anchorDay = anchorDay.plus({ days: 1 });
        }
        let cursor = rangeStart;
        while (cursor <= rangeEnd) {
          if (cursor.weekday === weekday) {
            const daysDiff = Math.floor(cursor.diff(anchorDay, 'days').days);
            if (daysDiff >= 0 && daysDiff % 14 === 0) {
              const iso = cursor.toISODate();
              if (iso) dates.push(iso);
            }
          }
          cursor = cursor.plus({ days: 1 });
        }
      }
    } else {
      const weekday = Number(row.scheduleRule ?? rangeStart.weekday);
      let cursor = rangeStart;
      while (cursor <= rangeEnd) {
        if (cursor.weekday === weekday) {
          const iso = cursor.toISODate();
          if (iso) dates.push(iso);
        }
        cursor = cursor.plus({ days: 1 });
      }
    }
  } else if (row.scheduleType === 'monthly') {
    const nth = /^nth:(-?[1-4]):([1-7])$/.exec(row.scheduleRule ?? '');
    if (nth?.[1] && nth[2]) {
      let month = rangeStart.startOf('month');
      const lastMonth = rangeEnd.startOf('month');
      while (month <= lastMonth) {
        const match = nthWeekdayOfMonth(month, Number(nth[1]), Number(nth[2]));
        if (match && match >= rangeStart && match <= rangeEnd) {
          const iso = match.toISODate();
          if (iso) dates.push(iso);
        }
        month = month.plus({ months: 1 });
      }
    }
  }

  return [...new Set(dates)].sort();
};

export const upcomingGatheringDates = (
  row: Pick<Gathering, 'scheduleType' | 'scheduleRule' | 'startsOn' | 'endsOn' | 'startTime' | 'endTime'>,
  today: string,
  limit = 5,
): Array<{ date: string; startTime: string; endTime: string }> => {
  const horizonEnd = DateTime.fromISO(today).plus({ days: 366 }).toISODate() ?? today;
  const dates = scheduleDatesForGathering(row, today, horizonEnd)
    .filter((date) => date >= today)
    .slice(0, limit);

  const startTime = row.startTime ?? '10:00';
  const endTime = row.endTime ?? '12:00';
  return dates.map((date) => ({ date, startTime, endTime }));
};
